import { Button, VStack, useDisclosure, useToast } from '@chakra-ui/react';
import { ArchiveVdcTier } from './ArchiveVdcTier';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { vdcTierTableColumns } from './Columns';
import { EditVdcTier } from './EditVdcTier';
import { CreateVdcTier } from './CreateVdcTier';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  TemplateRangeTier,
  templateRangeTiersApi,
} from '@texas/api/endpoints/metadata/templateRangeTiersApi';
import { DataTableContainer } from '@texas/components/shared/dataTable/DataTableContainer';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { ServerError } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { ConfigurationPage } from '@texas/components/configuration/layout/ConfigurationPage';
import { SearchFilterInput } from '@texas/components/SearchFilterInput';
import { TexasSwitch } from '@texas/components/shared/form/TexasSwitch';
import {
  SearchQuery,
  defaultSearchQuery,
} from '@texas/api/endpoints/metadata/types';

export function VdcTiers() {
  const {
    onOpen: onCreateOpen,
    onClose: onCreateClose,
    isOpen: isCreateOpen,
  } = useDisclosure();
  const {
    value: archiveValue,
    isOpen: isArchiveOpen,
    onOpen: onArchiveOpen,
    onClose: onArchiveClose,
  } = useValueDisclosure<TemplateRangeTier>();
  const {
    value: editValue,
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useValueDisclosure<number>();

  const { t } = useTranslation();
  const toast = useToast();

  const {
    data: tiers,
    refetch,
    error,
    loading,
  } = useApiResource(templateRangeTiersApi.getAll);

  const [searchQuery, setSearchQuery] =
    useState<SearchQuery>(defaultSearchQuery);

  const { request: restoreTierRequest } = useApiRequest(
    templateRangeTiersApi.restore,
  );

  useEffect(() => {
    refetch(searchQuery);
  }, [refetch, searchQuery]);

  const performRestore = useCallback(
    async (id: number) => {
      await request(
        restoreTierRequest,
        [id],
        () => {
          toast({
            title: t('general.restored'),
            status: 'success',
            isClosable: true,
          });
          refetch(searchQuery);
        },
        (error: ServerError) => {
          toast({
            title: t('general.restoreFailed'),
            description: error.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
    },
    [restoreTierRequest, toast, t, refetch, searchQuery],
  );

  const columns = useMemo(() => {
    return vdcTierTableColumns(t, onArchiveOpen, performRestore, onEditOpen);
  }, [t, onArchiveOpen, performRestore, onEditOpen]);

  return (
    <ConfigurationPage
      title={t('configuration.vdcTiers')}
      toolbar={
        <>
          <SearchFilterInput
            value={searchQuery.searchTerm}
            style="entityDirectory"
            onChange={(s) => setSearchQuery({ ...searchQuery, searchTerm: s })}
          />
          <TexasSwitch
            checked={searchQuery.includeArchived}
            label={t('general.includeArchived')}
            onChange={(checked) =>
              setSearchQuery({
                ...searchQuery,
                includeArchived: checked,
              })
            }
          />
        </>
      }
      action={
        <Button variant="entityDirectoryPrimary" onClick={onCreateOpen}>
          {t('general.addNew')}
        </Button>
      }
    >
      {archiveValue && (
        <ArchiveVdcTier
          tier={archiveValue}
          isOpen={isArchiveOpen}
          onClose={onArchiveClose}
          onArchive={() => {
            refetch(searchQuery);
          }}
        />
      )}

      <EditVdcTier
        tierId={editValue}
        isOpen={isEditOpen}
        onClose={onEditClose}
        onUpdated={() => {
          refetch(searchQuery);
        }}
      />

      <CreateVdcTier
        onCreated={() => {
          refetch(searchQuery);
          onCreateClose();
        }}
        onClose={onCreateClose}
        isOpen={isCreateOpen}
      />

      <VStack w="100%" alignItems="start">
        <DataTableContainer
          w="100%"
          datatable={{
            data: tiers ?? [],
            isLoading: loading,
            columns: columns,
            variant: 'entity-directory',
          }}
          error={error}
        />
      </VStack>
    </ConfigurationPage>
  );
}
